import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppTable } from '@/common/components';
import { AppRoutes, BaseQueryParams } from '@/common/interfaces';

import { RedirectsListTableDataObj, getRedirectSourceLabel, getRedirectTargetLabel, useGetAllRedirectsQuery } from '@/modules/Sites';

import { columns } from './Columns';

import './RedirectsListView.scss';

interface Props {
  siteId: string;
  currentPage: number;
  redirectsListItems: BaseQueryParams<typeof useGetAllRedirectsQuery>['data']['items'];
  redirectsListTotalItems: BaseQueryParams<typeof useGetAllRedirectsQuery>['data']['meta']['totalItems'];
  redirectsListItemsPerPage: BaseQueryParams<typeof useGetAllRedirectsQuery>['data']['meta']['itemsPerPage'];
  isLoading: boolean;
  handleChangePage: (page: number) => void;
}

export const RedirectsListView: FC<Props> = ({
  siteId,
  currentPage,
  redirectsListItems,
  redirectsListTotalItems,
  redirectsListItemsPerPage,
  isLoading,
  handleChangePage
}) => {
  const navigate = useNavigate();
  const redirectId = useParams()['*']?.split('/')[0];

  const goToRedirect = (id: string): void => {
    navigate(AppRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', id));
  };

  const dataSource = useMemo<RedirectsListTableDataObj[]>(
    () => redirectsListItems.map((redirect) => ({
      id: redirect.id,
      isActive: redirect.isActive,
      matchMode: redirect.matchMode,
      statusCode: redirect.statusCode,
      forVisitor: redirect.forVisitor,
      sourceLabel: getRedirectSourceLabel(redirect),
      targetLabel: getRedirectTargetLabel(redirect),
    })),
    [redirectsListItems]
  );

  return (
    <AppTable<RedirectsListTableDataObj>
      className="rules-table"
      rowKey="id"
      showHeader={ false }
      loading={ isLoading }
      dataSource={ dataSource }
      rowClassName={ (record) => (record.id === redirectId ? 'rules-table__row rules-table__row--selected' : 'rules-table__row') }
      onRow={ (record) => ({ onClick: () => goToRedirect(record.id) }) }
      pagination={{
        current: currentPage,
        total: redirectsListTotalItems,
        pageSize: redirectsListItemsPerPage,
        disabled: isLoading,
        onChange: handleChangePage,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
      columns={ columns }
    />
  )
};
